import { makeStyles } from '@mui/styles';
import Grid from 'apollo-react/components/Grid';
import Table from 'apollo-react/components/Table';
import classNames from 'classnames';
import React, { useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';

import {
  addTableRow,
  deleteTableRow,
  editTableRow,
  moveTableRow,
  setSelectedRow,
  validateSavedSearch,
} from './../actions/editor.actions';
import {
  COLUMNS_COLUMN,
  COLUMNS_IS_NAVIGATION_COLUMN,
  COLUMNS_SHOW_IN_GRID,
  COLUMNS_TRANSLATE_DATA,
  SAVED_SEARCH_COLUMNS,
  SAVED_SEARCH_COLUMNS_TABLE_LABEL,
} from './../constants/editor.constants';
import {
  getEntityTreeFieldsWithFilter,
  getIsDocRepository,
  getSavedSearchTableRows,
  getSelectedTableRow,
} from './../selectors/editor.selectors';
import ExpandablePanel from './ExpandablePanel';
import ActionCell from './table/ActionCell';
import CheckBoxCell from './table/CheckBoxCell';
import ColumnCell from './table/ColumnCell';
import { clearWarnings } from '../../actions/WarningActions';
import { useSavedSearchSelector, useTableLastRowVisibilityEffect } from '../../custom-hooks';
import { useTranslation } from '../../custom-hooks/useTranslation';
import { equalityFn } from '../../form-designer/utils/form-manager.utils';
import { getExportColumnsToDisplay } from '../utils/common.utils';

const useStyles = makeStyles({
  tableContainer: {
    overflowX: 'auto',
    '& [class*="middleAlign"]': {
      height: '60px !important',
    },
  },
  addButtonContainer: {
    textAlign: 'right',
    paddingTop: '8px !important',
    paddingRight: '15px !important',
  },
  select: {
    margin: 3,
    paddingRight: 3,
  },
  selected: {
    '& td': {
      backgroundColor: '#0768fd !important',
    },
  },
});

const columns = [
  {
    header: 'Column',
    accessor: COLUMNS_COLUMN,
    customCell: ColumnCell,
    width: 600,
  },
  {
    header: 'Show in Grid',
    accessor: COLUMNS_SHOW_IN_GRID,
    align: 'center',
    customCell: CheckBoxCell,
  },

  {
    header: 'Is Navigation Column',
    accessor: COLUMNS_IS_NAVIGATION_COLUMN,
    align: 'center',
    customCell: CheckBoxCell,
  },
  {
    header: 'Translate Data',
    accessor: COLUMNS_TRANSLATE_DATA,
    align: 'center',
    customCell: CheckBoxCell,
  },
  {
    accessor: 'action',
    customCell: ActionCell,
    align: 'right',
    width: 55,
  },
];

export const columnsTableValidationProps = [
  {
    prop: COLUMNS_COLUMN,
    errorLabel: 'Column',
    required: true,
    unique: true,
    tableName: SAVED_SEARCH_COLUMNS_TABLE_LABEL,
  },
];

const ColumnsTable = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const translateString = useTranslation();

  const entityColumns = useSelector(
    (state) => getEntityTreeFieldsWithFilter(state, { onlyRootEntityFields: true }),
    equalityFn
  );
  const selectedTableRowId = useSelector(getSelectedTableRow);
  const isDocRepository = useSelector(getIsDocRepository);
  const rows = useSavedSearchSelector(
    getSavedSearchTableRows,
    { tablePath: SAVED_SEARCH_COLUMNS },
    equalityFn
  );

  const tableRef = useRef();

  const tableName = SAVED_SEARCH_COLUMNS;

  useTableLastRowVisibilityEffect({ tableRef, rows });

  const columnsToDisplay = useMemo(
    () => getExportColumnsToDisplay({ columns, translateString, isDocRepository }),
    [translateString, isDocRepository]
  );

  const addRow = () => {
    const newRow = {
      rowId: uuidv4(),
      [COLUMNS_COLUMN]: '',
      [COLUMNS_SHOW_IN_GRID]: true,
      [COLUMNS_IS_NAVIGATION_COLUMN]: isDocRepository ? false : !rows.length,
      [COLUMNS_TRANSLATE_DATA]: false,
    };

    !rows.length && dispatch(clearWarnings());
    dispatch(addTableRow({ tableName, newRow }));
    dispatch(setSelectedRow({ rowId: newRow.rowId }));
  };

  const editRow = (params) => {
    dispatch(editTableRow({ ...params, tableName }));

    const requireValidation =
      params.error || [COLUMNS_IS_NAVIGATION_COLUMN, COLUMNS_SHOW_IN_GRID].includes(params.key);
    requireValidation && dispatch(validateSavedSearch());
  };

  const deleteRow = (params) => {
    dispatch(deleteTableRow({ ...params, tableName }));
  };

  const moveRow = (params) => {
    dispatch(moveTableRow({ ...params, tableName }));
  };

  const handleSelectRow = ({ rowId }) => {
    dispatch(setSelectedRow({ rowId }));
  };

  const infoTooltipText = isDocRepository
    ? "The system will automatically add the 'Document Number' column as a navigation column in runtime"
    : '';

  return (
    <ExpandablePanel
      label={translateString(SAVED_SEARCH_COLUMNS_TABLE_LABEL)}
      addRow={addRow}
      isDocRepository={isDocRepository}
      infoTooltipText={infoTooltipText}
    >
      <Grid xs={12} className={classes.tableContainer} ref={tableRef}>
        <Table
          columns={columnsToDisplay}
          emptyProps={{
            text: translateString('No data to display'),
          }}
          rowId="rowId"
          rows={rows.map((row, index) => ({
            ...row,
            classes,
            index,
            enableRowMovement: true,
            editRow,
            deleteRow,
            entityColumns,
            translateString,
            moveRow,
            handleSelectRow,
            rowsLength: rows.length,
            className: classNames(selectedTableRowId === row.rowId ? classes.selected : ''),
            selectedTableRowId,
            isSelected: selectedTableRowId === row.rowId,
          }))}
          hidePagination
        />
      </Grid>
    </ExpandablePanel>
  );
};

export default ColumnsTable;
